import { useEffect, useRef } from 'react';
import { useStore } from '../store';
import { api } from '../api/client';

export const useRealtimeSSE = () => {
  const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const handleLeadSSE = useStore((state) => state.handleLeadSSE);
  const handleDealSSE = useStore((state) => state.handleDealSSE);
  const handleClientSSE = useStore((state) => state.handleClientSSE);
  const handlePipelineSSE = useStore((state) => state.handlePipelineSSE);
  const isEditing = useStore((state) => state.isEditing);

  const connect = () => {
    try {
      const baseURL = api.defaults?.baseURL || 'http://127.0.0.1:5000';
      const token = localStorage.getItem('access_token');
      const orgId = localStorage.getItem('org_id');

      // Build query params for authentication (EventSource doesn't support custom headers)
      const params = new URLSearchParams();
      if (token) {
        params.append('token', token);
      }
      if (orgId) {
        params.append('org_id', orgId);
      }

      const url = `${baseURL}/public/stream${params.toString() ? `?${params.toString()}` : ''}`;
      const es = new EventSource(url);

      es.onopen = () => {
        console.log('[SSE] Connected');
        reconnectAttempts.current = 0;
      };

      es.onmessage = (evt) => {
        try {
          const message = JSON.parse(evt.data || '{}');
          const { type, data } = message;

          // Проверяем, не редактируется ли элемент
          const itemId =
            data?.lead?.id ||
            data?.lead?._id ||
            data?.deal?.id ||
            data?.deal?._id ||
            data?.client?.id ||
            data?.client?._id ||
            data?.id;

          if (itemId && isEditing(itemId)) {
            console.log(`[SSE] Skipping update for editing item: ${itemId}`);
            return;
          }

          // Роутинг событий к соответствующим slice handlers
          if (type?.startsWith('lead.')) {
            handleLeadSSE(message);
          } else if (type?.startsWith('deal.')) {
            handleDealSSE(message);
          } else if (type?.startsWith('client.')) {
            handleClientSSE(message);
          } else if (type?.startsWith('pipeline.')) {
            handlePipelineSSE(message);
          }
        } catch (error) {
          console.error('[SSE] Parse error:', error);
        }
      };

      es.onerror = (error) => {
        console.error('[SSE] Error:', error);
        es.close();

        // Переподключение с экспоненциальной задержкой
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            30000
          );
          reconnectAttempts.current++;

          console.log(
            `[SSE] Reconnecting... (attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS}) in ${delay}ms`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          console.error('[SSE] Max reconnect attempts reached. Giving up.');
        }
      };

      eventSourceRef.current = es;
    } catch (error) {
      console.error('[SSE] Connection error:', error);
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
    reconnect: connect,
  };
};
